import React, { useState, useEffect } from 'react';
import { PnevmatikaTable } from './components/PnevmatikaTable/PnevmatikaTable';
import { PnevmatikaForm } from './components/PnevmatikaForm';
import { PnevmatikaFiltersComponent } from './components/PnevmatikaFilters/PnevmatikaFilters';
import { Modal } from './components/common/Modal';
import { ConfirmationModal } from './components/common/ConfirmationModal';
import { DuplicateConfirmationModal } from './components/DuplicateConfirmationModal/DuplicateConfirmationModal';
import { Button } from './components/common/Button';
import { ThemeProvider } from './contexts/ThemeContext';
import ThemeToggle from './components/ThemeToggle/ThemeToggle';
import { Pnevmatika, PnevmatikaFilters, CreatePnevmatikaData, UpdatePnevmatikaData } from './types/pnevmatika';
import { pnevmatikeApi, authApi } from './services/api';
import { LoginForm } from './components/Auth/LoginForm';
import { ChangePasswordModal } from './components/Auth/ChangePasswordModal';
import './App.css';

function App() {
  const [user, setUser] = useState<{ id: number; username: string } | null>(null);
  const [pnevmatike, setPnevmatike] = useState<Pnevmatika[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<PnevmatikaFilters>({});
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPnevmatika, setEditingPnevmatika] = useState<Pnevmatika | undefined>();
  const [formLoading, setFormLoading] = useState(false);
  
  // Confirmation modal state
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [deletingPnevmatikaId, setDeletingPnevmatikaId] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Duplicate confirmation modal state
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [duplicatePnevmatike, setDuplicatePnevmatike] = useState<Pnevmatika[]>([]);
  const [pendingFormData, setPendingFormData] = useState<CreatePnevmatikaData | null>(null);
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);

  // Load pnevmatike on component mount and when filters change
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      // Minimal trust: treat as logged-in; backend will enforce validity
      setUser({ id: 0, username: 'admin' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload data when filters change
  useEffect(() => {
    if (user) {
      loadPnevmatike();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, user]);

  const loadPnevmatike = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await pnevmatikeApi.getAll(filters);
      setPnevmatike(data);
    } catch (err) {
      setError('Napaka pri nalaganju pnevmatik');
      console.error('Error loading pnevmatike:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    authApi.logout();
    setUser(null);
    setPnevmatike([]);
  };

  const handleEdit = (pnevmatika: Pnevmatika) => {
    setEditingPnevmatika(pnevmatika);
    setIsModalOpen(true);
  };

  const handleDelete = (id: number) => {
    setDeletingPnevmatikaId(id);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deletingPnevmatikaId === null) return;
    
    try {
      setDeleteLoading(true);
      await pnevmatikeApi.delete(deletingPnevmatikaId);
      await loadPnevmatike(); // Reload list
      setIsConfirmModalOpen(false);
      setDeletingPnevmatikaId(null);
    } catch (err) {
      setError('Napaka pri odstranjevanju pnevmatike');
      console.error('Error deleting pnevmatika:', err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setIsConfirmModalOpen(false);
    setDeletingPnevmatikaId(null);
  };

  const handleAddNew = () => {
    setEditingPnevmatika(undefined);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPnevmatika(undefined);
    // Also clear duplicate modal state if open
    if (isDuplicateModalOpen) {
      setIsDuplicateModalOpen(false);
      setDuplicatePnevmatike([]);
      setPendingFormData(null);
    }
  };

  const handleFormSubmit = async (data: CreatePnevmatikaData | UpdatePnevmatikaData, forceAdd = false) => {
    try {
      setFormLoading(true);
      setError(null);

      console.log('handleFormSubmit called with:', { data, forceAdd, editingPnevmatika: !!editingPnevmatika });

      if (editingPnevmatika) {
        // Update existing pnevmatika - check for duplicates unless forced
        if (!forceAdd) {
          const oznaka = (data as UpdatePnevmatikaData).oznaka_gume;
          console.log('Checking for duplicates on edit, oznaka:', oznaka, 'excludeId:', editingPnevmatika.id);
          if (oznaka !== undefined) {
            const duplicateCheck = await pnevmatikeApi.checkTireNumber(oznaka, editingPnevmatika.id);
            console.log('Duplicate check result:', duplicateCheck);
            if (duplicateCheck.exists && duplicateCheck.pnevmatike.length > 0) {
              // Show duplicate confirmation modal
              setDuplicatePnevmatike(duplicateCheck.pnevmatike);
              setPendingFormData(data as CreatePnevmatikaData); // This will be used with forceAdd=true
              setIsDuplicateModalOpen(true);
              setFormLoading(false);
              return;
            }
          }
        }
  await pnevmatikeApi.update(editingPnevmatika.id, data);
      } else {
        // Create new pnevmatika - check for duplicates unless forced
        if (!forceAdd) {
          const duplicateCheck = await pnevmatikeApi.checkTireNumber((data as CreatePnevmatikaData).oznaka_gume);
          if (duplicateCheck.exists && duplicateCheck.pnevmatike.length > 0) {
            // Show duplicate confirmation modal
            setDuplicatePnevmatike(duplicateCheck.pnevmatike);
            setPendingFormData(data as CreatePnevmatikaData);
            setIsDuplicateModalOpen(true);
            setFormLoading(false);
            return;
          }
        }
        
  await pnevmatikeApi.create(data as CreatePnevmatikaData);
      }

      await loadPnevmatike(); // Reload list
      handleCloseModal();
    } catch (err) {
      setError(editingPnevmatika ? 'Napaka pri posodabljanju pnevmatike' : 'Napaka pri dodajanju pnevmatike');
      console.error('Error submitting form:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDuplicateConfirm = async () => {
    if (!pendingFormData) return;
    
    try {
      setDuplicateLoading(true);
      // Force add the duplicate tire
      await handleFormSubmit(pendingFormData, true);
      setIsDuplicateModalOpen(false);
      setDuplicatePnevmatike([]);
      setPendingFormData(null);
    } catch (err) {
      setError('Napaka pri dodajanju pnevmatike');
      console.error('Error adding duplicate:', err);
    } finally {
      setDuplicateLoading(false);
    }
  };

  const handleDuplicateCancel = () => {
    setIsDuplicateModalOpen(false);
    setDuplicatePnevmatike([]);
    setPendingFormData(null);
  };

  // Filter handlers
  const handleFiltersChange = (newFilters: PnevmatikaFilters) => {
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    // Filters are applied automatically via useEffect when filters state changes
    // This handler can be used for additional logic if needed
  };

  const handleResetFilters = () => {
    setFilters({});
  };

  // Global keyboard shortcut: Shift+N => open Add New modal
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // Ignore when typing in inputs/textarea/select or contenteditable
      const target = e.target as HTMLElement | null;
      const isTyping = !!target && (
        target.isContentEditable ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
      );
      if (isTyping) return;

      // Only when logged in and no modal is already open
      if (!user) return;
      if (isModalOpen || isConfirmModalOpen || isDuplicateModalOpen || isChangePasswordOpen) return;

      // Shift+N
      if (e.shiftKey && e.code === 'KeyN') {
        e.preventDefault();
        handleAddNew();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [user, isModalOpen, isConfirmModalOpen, isDuplicateModalOpen, isChangePasswordOpen, handleAddNew]);

  if (!user) {
    return (
      <ThemeProvider>
        <div className="app">
          <header className="header">
            <div className="container">
              <h1>Shramba Pnevmatik</h1>
              <p>Prijavite se za nadaljevanje</p>
            </div>
          </header>
          <main className="container">
            <LoginForm onSuccess={(u) => { setUser(u); }} />
          </main>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <div className="app">
        <header className="header">
          <div className="container">
            <h1>Shramba Pnevmatik</h1>
            <p>Upravljanje avtomobilskih gum v shrambi</p>
          </div>
        </header>

        <main className="container">
          <div className="main-content">
            <div className="page-header">
              <h2>Pnevmatike v shrambi</h2>
              <div className="header-controls">
                <ThemeToggle />
                <Button variant="secondary" onClick={() => setIsChangePasswordOpen(true)}>
                  Spremeni geslo
                </Button>
                <Button variant="secondary" onClick={handleLogout}>
                  Odjava
                </Button>
                <Button onClick={handleAddNew}>
                  Dodaj novo pnevmatiko
                </Button>
              </div>
            </div>

            <PnevmatikaFiltersComponent
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onApplyFilters={handleApplyFilters}
              onResetFilters={handleResetFilters}
              loading={loading}
            />
            
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <PnevmatikaTable
              pnevmatike={pnevmatike}
              onEdit={handleEdit}
              onDelete={handleDelete}
              loading={loading}
            />
          </div>
        </main>

        {/* Add/Edit Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={editingPnevmatika ? 'Uredi pnevmatiko' : 'Dodaj novo pnevmatiko'}
        >
          <PnevmatikaForm
            pnevmatika={editingPnevmatika}
            onSubmit={handleFormSubmit}
            onCancel={handleCloseModal}
            loading={formLoading}
          />
        </Modal>

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={isConfirmModalOpen}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title="Odstrani pnevmatiko"
          message="Ali ste prepričani, da želite odstraniti to pnevmatiko iz shrambe? Tega dejanja ni mogoče razveljaviti."
          confirmText="Odstrani"
          cancelText="Prekliči"
          variant="danger"
          loading={deleteLoading}
        />

        {/* Duplicate Confirmation Modal */}
        <DuplicateConfirmationModal
          isOpen={isDuplicateModalOpen}
          onClose={handleDuplicateCancel}
          onConfirm={handleDuplicateConfirm}
          existingPnevmatike={duplicatePnevmatike}
          loading={duplicateLoading}
        />

        {/* Change Password Modal */}
        <ChangePasswordModal
          isOpen={isChangePasswordOpen}
          onClose={() => setIsChangePasswordOpen(false)}
        />
      </div>
    </ThemeProvider>
  );
}

export default App;
